import {
  AccessTimeOutlined,
  ApartmentOutlined,
  ChatOutlined,
  CheckRounded,
  CloseRounded,
  ContentCopyOutlined,
  EditOutlined,
  GppMaybeOutlined,
  Inventory2Outlined,
  PlaceOutlined,
  TimerOutlined,
  WarningAmberRounded,
} from '@mui/icons-material';
import { Box, Tooltip } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import ReactGA from 'react-ga4';
import { SCREEN_NAMES } from 'src/constants/screenNames';
import { DispatchCard, UnassignedItem, Vehicle } from 'src/entities/dispatch';
import { GeoAlertInfo } from 'src/utils/geoAlerts';

export type DispatchSelection =
  | {
      source: 'vehicle';
      card: DispatchCard;
      vehicle: Vehicle;
      orderIndex: number;
    }
  | {
      source: 'unassigned';
      item: UnassignedItem;
    }
  | null;

interface GeoUpdatePayload {
  newGeo: { lat: number; lng: number };
  address?: string;
  /** loading (積地) / unloading (降地) どちらの waypoint を更新するか */
  side: 'loading' | 'unloading';
  /** 対象 legRun ID (同一カードにマージされた全 legRun の ID を渡す) */
  legRunIds: number[];
}

interface DispatchDetailPanelProps {
  selection: DispatchSelection;
  hasDuplicate?: boolean;
  geoAlert?: GeoAlertInfo;
  onGeoUpdate?: (payload: GeoUpdatePayload) => void;
}

function SectionRow({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', gap: 1.5, py: 1.25 }}>
      <Box
        sx={{
          display: 'flex',
          height: 20,
          width: 20,
          flexShrink: 0,
          alignItems: 'center',
          justifyContent: 'center',
          color: 'hsl(var(--muted-foreground))',
          mt: '2px',
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1, fontSize: 14, lineHeight: 1.6, color: 'hsl(var(--foreground))' }}>
        {children}
      </Box>
    </Box>
  );
}

function TagList({ items }: { items: string[] }) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
      {items.map((item) => (
        <Box
          key={item}
          component="span"
          sx={{
            borderRadius: '4px',
            border: '1px solid',
            borderColor: 'hsl(var(--border) / 0.6)',
            bgcolor: 'hsl(var(--muted) / 0.4)',
            px: 1,
            py: '2px',
            fontSize: 12,
            color: 'hsl(var(--foreground) / 0.7)',
          }}
        >
          {item}
        </Box>
      ))}
    </Box>
  );
}

function GeoDisplay({ lat, lng, disabled, onGeoUpdate }: {
  lat: number;
  lng: number;
  /** 座標編集ボタン自体を disabled にする (legRun と紐付かないカード向け) */
  disabled?: boolean;
  onGeoUpdate?: (newGeo: { lat: number; lng: number }) => void;
}) {
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [invalid, setInvalid] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const geoText = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    ReactGA.event('click', {
      screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
      button_name: '座標コピー',
    });
    navigator.clipboard
      .writeText(geoText)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {
        /* ignore */
      });
  };

  const handleStartEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    ReactGA.event('click', {
      screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
      button_name: '座標編集',
    });
    setEditValue(geoText);
    setInvalid(false);
    setEditing(true);
  };

  const parseInput = (): { lat: number; lng: number } | null => {
    const parts = editValue.split(',').map((s) => s.trim());
    if (parts.length !== 2) return null;
    const newLat = parseFloat(parts[0]);
    const newLng = parseFloat(parts[1]);
    if (Number.isNaN(newLat) || Number.isNaN(newLng)) return null;
    if (Math.abs(newLat) > 90 || Math.abs(newLng) > 180) return null;
    return { lat: newLat, lng: newLng };
  };

  const handleConfirmEdit = () => {
    const parsed = parseInput();
    if (!parsed) {
      setInvalid(true);
      return;
    }
    ReactGA.event('apply', {
      screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
      button_name: '座標編集',
    });
    onGeoUpdate?.(parsed);
    setInvalid(false);
    setEditing(false);
  };

  const handleCancelEdit = () => {
    ReactGA.event('close', {
      screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
      button_name: '座標編集',
    });
    setInvalid(false);
    setEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      handleConfirmEdit();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelEdit();
    }
  };

  if (editing) {
    return (
      <Box
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, ml: 0.5 }}
        onClick={(e) => e.stopPropagation()}
      >
        <PlaceOutlined sx={{ fontSize: 12, color: 'hsl(var(--muted-foreground))' }} />
        <Box
          component="input"
          ref={inputRef}
          value={editValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setEditValue(e.target.value);
            if (invalid) setInvalid(false);
          }}
          onKeyDown={handleKeyDown}
          placeholder="lat, lng"
          sx={{
            width: 180,
            borderRadius: '4px',
            border: '1px solid',
            borderColor: invalid ? 'hsl(var(--status-error-accent))' : 'hsl(var(--border))',
            bgcolor: 'hsl(var(--background))',
            px: 0.75,
            py: '2px',
            fontSize: 10,
            fontFamily: 'monospace',
            color: 'hsl(var(--foreground))',
            outline: 'none',
            '&:focus': {
              boxShadow: `0 0 0 1px ${invalid ? 'hsl(var(--status-error-accent))' : 'hsl(var(--ring))'}`,
            },
          }}
        />
        <Tooltip title="確定 (Enter)" placement="top" enterDelay={200}>
          <Box
            component="button"
            type="button"
            onClick={handleConfirmEdit}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 18,
              width: 18,
              borderRadius: '4px',
              color: 'hsl(var(--status-success-foreground))',
              bgcolor: 'hsl(var(--status-success-accent) / 0.12)',
              border: '1px solid hsl(var(--status-success-accent) / 0.4)',
              cursor: 'pointer',
              p: 0,
              transition: 'background-color 150ms',
              '&:hover': { bgcolor: 'hsl(var(--status-success-accent) / 0.22)' },
            }}
          >
            <CheckRounded sx={{ fontSize: 12 }} />
          </Box>
        </Tooltip>
        <Tooltip title="キャンセル (Esc)" placement="top" enterDelay={200}>
          <Box
            component="button"
            type="button"
            onClick={handleCancelEdit}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 18,
              width: 18,
              borderRadius: '4px',
              color: 'hsl(var(--muted-foreground))',
              bgcolor: 'hsl(var(--muted) / 0.4)',
              border: '1px solid hsl(var(--border))',
              cursor: 'pointer',
              p: 0,
              transition: 'background-color 150ms',
              '&:hover': { bgcolor: 'hsl(var(--muted) / 0.7)' },
            }}
          >
            <CloseRounded sx={{ fontSize: 12 }} />
          </Box>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '2px', ml: 0.5 }}>
      <Tooltip title={copied ? 'コピーしました' : 'クリックしてコピー'} placement="top" enterDelay={200}>
        <Box
          component="button"
          type="button"
          onClick={handleCopy}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            fontSize: 10,
            fontFamily: 'monospace',
            color: 'hsl(var(--muted-foreground))',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            p: 0,
            transition: 'color 150ms',
            '&:hover': { color: 'hsl(var(--foreground))' },
          }}
        >
          <PlaceOutlined sx={{ fontSize: 12 }} />
          <span>{geoText}</span>
          {copied
            ? <CheckRounded sx={{ fontSize: 12, color: 'hsl(var(--status-success-accent))' }} />
            : <ContentCopyOutlined sx={{ fontSize: 12 }} />}
        </Box>
      </Tooltip>
      {!disabled && onGeoUpdate && (
        <Tooltip title="座標を編集" placement="top" enterDelay={200}>
          <Box
            component="button"
            type="button"
            onClick={handleStartEdit}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 16,
              width: 16,
              color: 'hsl(var(--muted-foreground))',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              p: 0,
              transition: 'color 150ms',
              '&:hover': { color: 'hsl(var(--foreground))' },
            }}
          >
            <EditOutlined sx={{ fontSize: 10 }} />
          </Box>
        </Tooltip>
      )}
    </Box>
  );
}

const dividerSx = { borderTop: '1px solid', borderColor: 'hsl(var(--border) / 0.4)' } as const;

/**
 * 小数点以下 2 桁で丸めつつ、末尾の 0 は除去する。
 * 217.78199999999998 → "217.78"
 * 26 → "26"
 * 26.5 → "26.5"
 */
function formatNumber2(value: number): string {
  return parseFloat(value.toFixed(2)).toString();
}

export function DispatchDetailPanel({ selection, hasDuplicate = false, geoAlert, onGeoUpdate }: DispatchDetailPanelProps) {
  if (!selection) {
    return (
      <Box
        sx={{
          borderRadius: 2,
          border: '1px dashed',
          borderColor: 'hsl(var(--border))',
          bgcolor: 'hsl(var(--secondary) / 0.3)',
          px: 3,
          py: 5,
          textAlign: 'center',
          fontSize: 14,
          color: 'hsl(var(--muted-foreground))',
        }}
      >
        カードを選択すると詳細を確認できます。
      </Box>
    );
  }

  const title =
    selection.source === 'vehicle'
      ? selection.card.type === 'loading'
        ? selection.card.locationName ?? '積み込み拠点'
        : selection.card.storeName
      : selection.item.storeName;

  const card = selection.source === 'vehicle' ? selection.card : null;
  const deliveryCard = card?.type === 'delivery' ? card : null;
  const loadingCard = card?.type === 'loading' ? card : null;
  const unassignedItem = selection.source === 'unassigned' ? selection.item : null;

  const address = deliveryCard?.storeAddress ?? loadingCard?.address ?? unassignedItem?.storeAddress ?? undefined;
  const geo = deliveryCard?.geo ?? loadingCard?.geo ?? unassignedItem?.geo ?? undefined;

  // 座標編集の対象 side と legRunIds を決定する
  // - delivery カード (降地): side=unloading + card.legRunIds
  // - loading カード (積地):   side=loading + card.legRunIds
  // - unassigned / no-delivery / fr-template 等: 編集不可
  const geoEditSide: 'loading' | 'unloading' | null = deliveryCard
    ? (deliveryCard.status === 'matched' || deliveryCard.status === 'template' ? 'unloading' : null)
    : loadingCard
      ? 'loading'
      : null;
  const geoEditLegRunIds: number[] = geoEditSide === 'unloading'
    ? (deliveryCard?.legRunIds ?? [])
    : geoEditSide === 'loading'
      ? (loadingCard?.legRunIds ?? [])
      : [];
  const canEditGeo = geoEditSide != null && geoEditLegRunIds.length > 0;

  const scheduledDate = deliveryCard?.scheduledDate ?? loadingCard?.scheduledDate ?? unassignedItem?.scheduledDate ?? '';
  const scheduledTime = deliveryCard?.scheduledTime ?? loadingCard?.scheduledTime ?? unassignedItem?.scheduledTime ?? '';
  const workDuration = deliveryCard?.workDuration ?? loadingCard?.workDuration ?? unassignedItem?.workDuration ?? null;

  const cases = deliveryCard?.todayCases ?? deliveryCard?.cases ?? loadingCard?.cases ?? unassignedItem?.cases ?? null;
  const weight = deliveryCard?.weight ?? loadingCard?.weight ?? unassignedItem?.weight ?? null;
  const volume = deliveryCard?.volume ?? loadingCard?.volume ?? unassignedItem?.volume ?? null;
  const invoiceCount = deliveryCard?.invoiceCount ?? loadingCard?.invoiceCount ?? unassignedItem?.invoiceCount ?? null;

  const remarks = deliveryCard?.remarks ?? loadingCard?.remarks ?? unassignedItem?.remarks ?? null;
  const shippers = deliveryCard?.shippers ?? loadingCard?.shippers ?? unassignedItem?.shippers ?? [];
  const designatedTimeWindow = deliveryCard?.designatedTimeWindow ?? loadingCard?.designatedTimeWindow ?? unassignedItem?.designatedTimeWindow ?? null;
  const constraints = deliveryCard?.constraints ?? loadingCard?.constraints ?? unassignedItem?.constraints ?? [];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ pb: 1.5 }}>
        <Box sx={{ fontSize: 16, fontWeight: 600, color: 'hsl(var(--foreground))', lineHeight: 1.3 }}>{title}</Box>
        {address && (
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 0.5, mt: '2px' }}>
            <Box component="span" sx={{ fontSize: 12, color: 'hsl(var(--foreground) / 0.7)' }}>
              {address}
            </Box>
            {geo && (
              <GeoDisplay
                lat={geo.lat}
                lng={geo.lng}
                disabled={!canEditGeo}
                onGeoUpdate={canEditGeo && geoEditSide
                  ? (newGeo) => onGeoUpdate?.({ newGeo, address, side: geoEditSide, legRunIds: geoEditLegRunIds })
                  : undefined}
              />
            )}
          </Box>
        )}
        {!address && geo && (
          <Box sx={{ mt: '2px' }}>
            <GeoDisplay
              lat={geo.lat}
              lng={geo.lng}
              disabled={!canEditGeo}
              onGeoUpdate={canEditGeo && geoEditSide
                ? (newGeo) => onGeoUpdate?.({ newGeo, side: geoEditSide, legRunIds: geoEditLegRunIds })
                : undefined}
            />
          </Box>
        )}
        {geoAlert && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              mt: 0.75,
              borderRadius: '4px',
              px: 1,
              py: 0.5,
              fontSize: 11,
              lineHeight: 1.2,
              bgcolor:
                geoAlert.level === 'strong'
                  ? 'hsl(var(--destructive) / 0.08)'
                  : 'hsl(var(--status-warning-accent) / 0.08)',
              color:
                geoAlert.level === 'strong'
                  ? 'hsl(var(--status-error-accent))'
                  : 'hsl(var(--status-warning-foreground))',
            }}
          >
            <WarningAmberRounded sx={{ fontSize: 12, flexShrink: 0 }} />
            <span>{geoAlert.message}</span>
          </Box>
        )}
        {selection.source === 'vehicle' && (
          <Box sx={{ fontSize: 12, color: 'hsl(var(--foreground) / 0.7)', mt: 0.5 }}>
            {selection.vehicle.companyName} ・ {selection.vehicle.vehicleNumber} ・ {selection.vehicle.driverName}
          </Box>
        )}
      </Box>

      {/* Schedule */}
      {(scheduledDate || scheduledTime) && (
        <Box sx={{ display: 'flex', gap: 1.5, py: 1.5, ...dividerSx }}>
          <Box
            sx={{
              display: 'flex',
              height: 20,
              width: 20,
              flexShrink: 0,
              alignItems: 'center',
              justifyContent: 'center',
              color: 'hsl(var(--muted-foreground))',
              mt: '2px',
            }}
          >
            <AccessTimeOutlined sx={{ fontSize: 16 }} />
          </Box>
          <Box sx={{ fontSize: 14, color: 'hsl(var(--foreground))', lineHeight: 1.6 }}>
            {scheduledDate && <span>{scheduledDate}</span>}
            {scheduledDate && scheduledTime && <Box component="span" sx={{ mx: 0.5 }}>⋅</Box>}
            {scheduledTime && <Box component="span" sx={{ fontWeight: 500 }}>{scheduledTime}</Box>}
            {workDuration != null && (
              <Box component="span" sx={{ color: 'hsl(var(--foreground) / 0.6)', ml: 0.75 }}>
                作業時間{workDuration}分
              </Box>
            )}
          </Box>
        </Box>
      )}

      <Box sx={dividerSx} />

      <SectionRow icon={<Inventory2Outlined sx={{ fontSize: 16 }} />}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 2, rowGap: 0.5 }}>
          <Box>
            <Box component="span" sx={{ color: 'hsl(var(--foreground) / 0.7)', fontSize: 12 }}>数量</Box>
            <Box component="span" sx={{ ml: 1, fontWeight: 500 }}>{cases != null ? `${cases} cs` : '--'}</Box>
          </Box>
          <Box>
            <Box component="span" sx={{ color: 'hsl(var(--foreground) / 0.7)', fontSize: 12 }}>重量</Box>
            <Box component="span" sx={{ ml: 1, fontWeight: 500 }}>{weight != null ? `${formatNumber2(weight)} kg` : '--'}</Box>
          </Box>
          <Box>
            <Box component="span" sx={{ color: 'hsl(var(--foreground) / 0.7)', fontSize: 12 }}>体積</Box>
            <Box component="span" sx={{ ml: 1, fontWeight: 500 }}>{volume != null ? `${formatNumber2(volume)} m³` : '--'}</Box>
          </Box>
          <Box>
            <Box component="span" sx={{ color: 'hsl(var(--foreground) / 0.7)', fontSize: 12 }}>伝票枚数</Box>
            <Box component="span" sx={{ ml: 1, fontWeight: 500 }}>{invoiceCount != null ? `${invoiceCount} 枚` : '--'}</Box>
          </Box>
        </Box>
      </SectionRow>

      {/* 積地カードでは備考・荷主名は情報量が多くなりがちなので非表示 */}
      {!loadingCard && (
        <>
          <Box sx={dividerSx} />

          <SectionRow icon={<ChatOutlined sx={{ fontSize: 16 }} />}>
            <Box component="span" sx={!remarks ? { color: 'hsl(var(--muted-foreground))' } : undefined}>
              {remarks || '備考なし'}
            </Box>
          </SectionRow>

          <Box sx={dividerSx} />

          <SectionRow icon={<ApartmentOutlined sx={{ fontSize: 16 }} />}>
            {shippers.length > 0 ? (
              <TagList items={shippers} />
            ) : (
              <Box component="span" sx={{ color: 'hsl(var(--muted-foreground))' }}>荷主情報なし</Box>
            )}
          </SectionRow>
        </>
      )}

      <Box sx={dividerSx} />

      <SectionRow icon={<TimerOutlined sx={{ fontSize: 16 }} />}>
        <Box>
          <Box component="span" sx={{ color: 'hsl(var(--foreground) / 0.7)', fontSize: 12 }}>指定時間：</Box>
          <Box component="span" sx={{ fontWeight: 500, fontSize: 14 }}>
            {designatedTimeWindow ? designatedTimeWindow : 'なし'}
          </Box>
        </Box>
      </SectionRow>

      <Box sx={dividerSx} />

      <SectionRow icon={<GppMaybeOutlined sx={{ fontSize: 16 }} />}>
        {constraints.length > 0 ? (
          <TagList items={constraints} />
        ) : (
          <Box component="span" sx={{ color: 'hsl(var(--muted-foreground))' }}>制約なし</Box>
        )}
        {hasDuplicate && selection.source === 'vehicle' && selection.card.type === 'delivery' && (
          <Box sx={{ mt: 0.75, fontSize: 12, color: 'hsl(var(--destructive))' }}>
            ⚠ 同一納品先が他車両にも割り当て済み
          </Box>
        )}
      </SectionRow>
    </Box>
  );
}
